/**
 * Simplified Secure Storage for authentication tokens
 * Provides encrypted storage for access and refresh tokens
 */

import { STORAGE_KEYS, User } from '../types/auth';
import { logger } from '../utils/logger';

// Simple encryption key - in production, this should be more secure
// For demo purposes, we're using a fixed key. In real apps, use proper key management.
const ENCRYPTION_KEY = 'valve-control-api-encryption-key-2024';

/**
 * Simple AES-like encryption for token storage
 * Note: This is a simplified implementation for demonstration.
 * In production, use proper crypto libraries like Web Crypto API.
 */
class SimpleEncryption {
  /**
   * Encrypt a string using a simple XOR cipher
   * @param text The text to encrypt
   * @returns Encrypted base64 string
   */
  static encrypt(text: string): string {
    // Convert to base64 first
    const base64 = btoa(text);
    let encrypted = '';
    
    for (let i = 0; i < base64.length; i++) {
      const charCode = base64.charCodeAt(i);
      const keyChar = ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length);
      encrypted += String.fromCharCode(charCode ^ keyChar);
    }
    
    return btoa(encrypted);
  }

  /**
   * Decrypt a string using simple XOR cipher
   * @param encryptedText The encrypted base64 string
   * @returns Decrypted string
   */
  static decrypt(encryptedText: string): string {
    try {
      const encrypted = atob(encryptedText);
      let decrypted = '';
      
      for (let i = 0; i < encrypted.length; i++) {
        const charCode = encrypted.charCodeAt(i);
        const keyChar = ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length);
        decrypted += String.fromCharCode(charCode ^ keyChar);
      }
      
      return atob(decrypted);
    } catch (error) {
      logger.error('Failed to decrypt token', error);
      throw new Error('Invalid encrypted data');
    }
  }
}

/**
 * SecureStorage class for encrypted token storage
 * Simplified implementation that focuses on basic encryption
 */
export class SecureStorage {
  /**
   * Store authentication tokens securely
   * @param accessToken The JWT access token
   * @param refreshToken The JWT refresh token
   * @param expiresAt Token expiration timestamp
   */
  static setTokens(accessToken: string, refreshToken: string, expiresAt: number): void {
    try {
      const encryptedAccessToken = SimpleEncryption.encrypt(accessToken);
      const encryptedRefreshToken = SimpleEncryption.encrypt(refreshToken);
      
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, encryptedAccessToken);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, encryptedRefreshToken);
      localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, expiresAt.toString());
      
      logger.debug('Tokens stored securely');
    } catch (error) {
      logger.error('Failed to store tokens', error);
      throw new Error('Failed to store tokens securely');
    }
  }

  /**
   * Get the access token
   * @returns The access token or null if not found/invalid
   */
  static getAccessToken(): string | null {
    try {
      const encryptedToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      
      if (!encryptedToken) {
        return null;
      }
      
      // Check for obviously corrupted data
      if (encryptedToken.length < 10 || encryptedToken === 'undefined' || encryptedToken === 'null') {
        this.clearTokens();
        return null;
      }
      
      const decryptedToken = SimpleEncryption.decrypt(encryptedToken);
      
      // Validate decrypted token format
      if (!decryptedToken || decryptedToken.length < 10 || !decryptedToken.includes('.')) {
        this.clearTokens();
        return null;
      }
      
      return decryptedToken;
    } catch (error) {
      logger.error('Failed to get access token', error);
      this.clearTokens(); // Clear potentially corrupted data
      return null;
    }
  }

  /**
   * Get the refresh token
   * @returns The refresh token or null if not found/invalid
   */
  static getRefreshToken(): string | null {
    try {
      const encryptedToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!encryptedToken) {
        return null;
      }
      
      // Check for obviously corrupted data
      if (encryptedToken.length < 10 || encryptedToken === 'undefined' || encryptedToken === 'null') {
        this.clearTokens();
        return null;
      }
      
      const decryptedToken = SimpleEncryption.decrypt(encryptedToken);
      
      // Validate decrypted token format
      if (!decryptedToken || decryptedToken.length < 10 || !decryptedToken.includes('.')) {
        this.clearTokens();
        return null;
      }
      
      return decryptedToken;
    } catch (error) {
      logger.error('Failed to get refresh token', error);
      this.clearTokens(); // Clear potentially corrupted data
      return null;
    }
  }

  /**
   * Get token expiration timestamp
   * @returns Expiration timestamp or null if not found
   */
  static getTokenExpiration(): number | null {
    const expiration = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
    return expiration ? parseInt(expiration, 10) : null;
  }

  /**
   * Check if access token is expired or will expire soon
   * @param thresholdSeconds Seconds before expiration to consider as expiring
   * @returns True if token is expired or expiring soon
   */
  static isTokenExpiring(thresholdSeconds: number = 60): boolean {
    const expiration = this.getTokenExpiration();
    if (!expiration) {
      return true; // No expiration info means we should refresh
    }
    
    const now = Math.floor(Date.now() / 1000);
    return (expiration - now) <= thresholdSeconds;
  }

  /**
   * Clear all stored tokens
   */
  static clearTokens(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
    localStorage.removeItem(STORAGE_KEYS.USER_INFO);
    
    logger.debug('All tokens cleared from secure storage');
  }

  /**
   * Store user information
   * @param user The user object to store
   */
  static setUser(user: User): void {
    try {
      const userJson = JSON.stringify(user);
      const encryptedUser = SimpleEncryption.encrypt(userJson);
      localStorage.setItem(STORAGE_KEYS.USER_INFO, encryptedUser);
    } catch (error) {
      logger.error('Failed to store user info', error);
      throw new Error('Failed to store user information');
    }
  }

  /**
   * Get stored user information
   * @returns The user object or null if not found
   */
  static getUser(): User | null {
    try {
      const encryptedUser = localStorage.getItem(STORAGE_KEYS.USER_INFO);
      if (!encryptedUser) {
        return null;
      }
      
      const userJson = SimpleEncryption.decrypt(encryptedUser);
      return JSON.parse(userJson);
    } catch (error) {
      logger.error('Failed to get user info', error);
      return null;
    }
  }

  /**
   * Check if valid tokens exist
   * @returns True if both access and refresh tokens exist
   */
  static hasValidTokens(): boolean {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    
    return !!(accessToken && refreshToken);
  }
}

export default SecureStorage;